import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, X, Sparkle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Universal idea suggester. Drop into any creator page:
 * <IdeaSuggester creator="visual_ad" onPick={(idea) => prefillFields(idea)} />
 *
 * `creator` must be one of: visual_ad, flyer, carousel, email, animation, product_shoot
 *
 * Each idea object varies by creator; consumer renders it back into form fields.
 */
export default function IdeaSuggester({ creator, extraContext = '', onPick, label = 'Suggest ideas', compact = false }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [historyCount, setHistoryCount] = useState(0);

  const fetchIdeas = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/ideas/suggest`, {
        creator,
        extra_context: extraContext,
        count: 6,
      });
      setIdeas(data.ideas || []);
      setHistoryCount(data.history_count || 0);
      setOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not fetch ideas');
    } finally {
      setLoading(false);
    }
  };

  const renderIdeaCard = (idea, i) => {
    // Render varies a bit per creator; keep the most useful fields visible
    const title = idea.headline || idea.title || idea.subject || idea.concept_name || `Idea ${i + 1}`;
    const sub = idea.subhead || idea.subtext || idea.angle || idea.outline || idea.scene_description || idea.content_arc || '';
    const reason = idea.reason || '';
    const tag = idea.style || idea.shoot_style || idea.cta || '';

    const onClick = () => {
      // Brand DNA feedback loop — record the pick so the next batch leans into it
      axios.post(`${API}/api/assets/track-pick`, {
        creator, idea, ts: new Date().toISOString(),
      }).catch(() => {});
      onPick?.(idea);
      setOpen(false);
    };

    return (
      <button
        key={i}
        onClick={onClick}
        data-testid={`idea-card-${i}`}
        className="w-full text-left rounded-xl p-4 transition-all hover:ring-1 hover:ring-white/15 group"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-base font-semibold" style={{ color: '#c8f060' }}>{title}</p>
          {tag && (
            <span className="text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded shrink-0"
              style={{ background: 'rgba(200,240,96,0.08)', color: 'rgba(200,240,96,0.7)' }}>{tag}</span>
          )}
        </div>
        {Array.isArray(sub) ? (
          <ul className="mt-2 space-y-1">
            {sub.map((line, j) => (
              <li key={j} className="text-xs flex gap-1.5" style={{ color: 'rgba(245,240,232,0.65)' }}>
                <span style={{ color: '#c8f060' }}>·</span>{line}
              </li>
            ))}
          </ul>
        ) : (
          sub && <p className="text-xs mt-1.5" style={{ color: 'rgba(245,240,232,0.65)' }}>{sub}</p>
        )}
        {reason && <p className="text-[11px] mt-2 italic" style={{ color: 'rgba(245,240,232,0.4)' }}>{reason}</p>}
      </button>
    );
  };

  return (
    <>
      <button
        onClick={fetchIdeas}
        disabled={loading}
        data-testid={`idea-suggester-${creator}`}
        className={`${compact ? 'px-3 py-1.5 text-[11px]' : 'px-4 py-2 text-xs'} rounded-lg font-semibold flex items-center gap-1.5 transition-all disabled:opacity-50 hover:scale-[1.02]`}
        style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.2)' }}
      >
        {loading ? (
          <div className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} border-2 rounded-full animate-spin`} style={{ borderColor: 'rgba(200,240,96,0.2)', borderTopColor: '#c8f060' }} />
        ) : (
          <Lightbulb size={compact ? 12 : 14} weight="fill" />
        )}
        {label}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            style={{ background: 'rgba(0,0,0,0.85)' }}
            data-testid="idea-suggester-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-2xl rounded-2xl p-6 my-auto"
              style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Sparkle size={18} weight="fill" style={{ color: '#c8f060' }} />
                  <p className="text-base font-semibold" style={{ color: '#f5f0e8' }}>Ideas for you</p>
                </div>
                <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-white/5">
                  <X size={16} style={{ color: 'rgba(245,240,232,0.5)' }} />
                </button>
              </div>
              <p className="text-[11px] mb-4" style={{ color: 'rgba(245,240,232,0.4)' }}>
                Based on your brand profile{historyCount > 0 ? ` + your last ${historyCount} ${creator.replace('_', ' ')}s` : ''}. Tap one to fill the form.
              </p>

              {ideas.length === 0 ? (
                <p className="text-sm py-8 text-center" style={{ color: 'rgba(245,240,232,0.4)' }}>
                  Couldn't generate ideas right now — try again.
                </p>
              ) : (
                <div className="space-y-2.5">
                  {ideas.map((idea, i) => renderIdeaCard(idea, i))}
                </div>
              )}

              <button onClick={fetchIdeas} disabled={loading} className="mt-4 w-full py-2 rounded-lg text-xs font-medium" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.7)' }}>
                {loading ? 'Generating fresh ideas…' : 'Generate fresh ideas'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
